'use client';

import { ImageIcon } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useState, useCallback, useMemo } from 'react';
import { RespImage } from '@/app/_components/core/resp-image';
import type { ImageGalleryItem } from '@/app/_components/shared/image-gallery/image-gallery.types';
import { getItemTrackId } from '@/app/_components/shared/utils/component-helpers';
import { emptyToUndefined } from '@/app/_lib/utils';
import type { LightboxConfig, ImageGalleryLabels } from '@/entities/models/page';

// Lazy load lightbox - only loaded when user clicks an image
const GalleryLightbox = dynamic(() => import('./gallery-lightbox').then((mod) => mod.GalleryLightbox), {
  ssr: false,
});

// Image sizes for 3-column (mobile) / 4-column (lg+) grid within max-w-3xl
// lg+ (4 cols, 768px - 48px gap) / 4 = 180px
// md (3 cols, 736px - 24px gap) / 3 = 237px
// sm (3 cols, 608px - 24px gap) / 3 = 195px
// mobile (3 cols, 100vw - 32px padding - 24px gap) / 3
const IMAGE_SIZES =
  '(min-width: 1024px) 180px, (min-width: 768px) 237px, (min-width: 640px) 195px, calc((100vw - 56px) / 3)';

interface ImageGalleryGridProps {
  items: ImageGalleryItem[];
  initialVisibleCount: number;
  lightboxConfig?: LightboxConfig;
  labels?: ImageGalleryLabels;
  priorityFirstItem?: boolean;
  trackingPrefix?: string;
}

export function ImageGalleryGrid({
  items,
  initialVisibleCount,
  lightboxConfig,
  labels,
  priorityFirstItem,
  trackingPrefix,
}: ImageGalleryGridProps) {
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const showMoreLabel = emptyToUndefined(labels?.showMoreLabel) ?? 'Mehr anzeigen';
  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  // Only items with a full-size version can open the lightbox. The lightbox
  // receives exactly these, so a tile's index into this list is its slide index.
  const openableItems = useMemo(
    () => items.filter((item) => item.fullSize),
    [items]
  );

  const handleShowMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + initialVisibleCount, items.length));
  }, [initialVisibleCount, items.length]);

  const handleImageClick = useCallback((index: number) => {
    setLightboxIndex(index);
  }, []);

  const handleLightboxClose = useCallback(() => {
    setLightboxIndex(-1);
  }, []);

  return (
    <>
      <div className="grid grid-cols-3 gap-3 lg:grid-cols-4 lg:gap-4">
        {visibleItems.map((item, index) => {
            const tileContent =
              item.srcsetCandidates.length > 0 ? (
                <RespImage
                  srcsetCandidates={item.srcsetCandidates}
                  alt={item.altText ?? ''}
                  title={item.title ?? undefined}
                  sizes={IMAGE_SIZES}
                  priority={priorityFirstItem && index === 0}
                  className="size-full object-cover transition-transform duration-300 [@media(hover:hover)]:group-hover:scale-105"
                />
              ) : (
                <div className="flex size-full items-center justify-center bg-muted">
                  <ImageIcon className="size-8 text-muted-foreground" />
                </div>
              );

            // No full-size version → static, non-clickable tile (no button, no
            // cursor, no focus ring, no click tracking). The image still shows.
            if (!item.fullSize) {
              return (
                <div
                  key={`gallery-${item.id}`}
                  className="relative aspect-square overflow-hidden rounded-image-gallery-thumbnail"
                >
                  {tileContent}
                </div>
              );
            }

            // Clickable: open the lightbox at this item's slide index.
            const openIndex = openableItems.indexOf(item);
            return (
              <button
                key={`gallery-${item.id}`}
                type="button"
                className="group relative aspect-square cursor-pointer overflow-hidden rounded-image-gallery-thumbnail focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                onClick={() => { handleImageClick(openIndex); }}
                aria-label={emptyToUndefined(item.altText) ?? `Bild ${index + 1} vergroessern`}
                data-track-id={
                  trackingPrefix
                    ? `${trackingPrefix}-item-${getItemTrackId(item.id, index)}`
                    : undefined
                }
              >
                {tileContent}
              </button>
            );
          })}
      </div>

      {hasMore && (
        <div className="mt-6">
          <button
            type="button"
            onClick={handleShowMore}
            className="cursor-pointer text-primary underline underline-offset-2"
            aria-label={`${showMoreLabel} (${items.length - visibleCount} weitere)`}
            data-track-id={trackingPrefix ? `${trackingPrefix}-show-more` : undefined}
          >
            {showMoreLabel}
          </button>
        </div>
      )}

      {lightboxIndex >= 0 && (
        <GalleryLightbox
          items={openableItems}
          index={lightboxIndex}
          open={true}
          onClose={handleLightboxClose}
          config={lightboxConfig}
          labels={labels}
        />
      )}
    </>
  );
}
